import { useEffect, useRef, useCallback } from "react";
import doDebounce from "lodash/debounce";
import { useLazyQuery } from "@apollo/react-hooks";
import isEqual from "lodash/isEqual";
import { useAuthDataContext } from "components/auth";
import { HTTP_ERROR_CODE_403 } from "../../../utils/constants";

/**
 * Hook para usar una query Graphql refrescable, con información de estado de carga.
 *
 * @param {Object} options - opciones del hook.
 * @param {GqlQuery} options.query - query que se va a ejecutar.
 * @param {Object} options.variables - objeto con las variables que necesita la query inicial.
 * @param {Object} options.debounceFirst - indica si la primera petición debe ser ejecutada
 * inmediatamente (false) o "debounceada" (true). Por defecto false.
 * @param {Object} options.debounce - indica si las peticiones deben ser ejecutadas
 * inmediatamente (false) o "debounceadas" (true). Por defecto true.
 * @param {Object} options.skip - indica que se debe ignorar la query.
 * @param {Object} options.pollInterval - intervalo de polling en ms.
 * @param {Object} options.onFinishedQuery - callback que se invoca cuando termina la query, envía el parámetro success:{Boolean}.
 * @return {Object} - un objeto con los campos:
 *   * {Object} data -  objeto de datos de la respuesta.
 *   * {boolean} loading - estado de carga de la query.
 *   * {Object} error -  objeto de error de la respuesta, si se produjo.
 */
export const useQuery = ({
  query,
  variables,
  debounceFirst = false,
  debounce = false,
  skip = false,
  pollInterval = 0,
  onFinishedQuery = () => {},
}) => {
  const { onRefreshToken } = useAuthDataContext();
  const [doLazyQuery, { data = {}, loading, error, called, variables: calledVariables, refetch }] = useLazyQuery(
    query,
    { pollInterval },
  );
  const isFirst = useRef(true);

  // On every "doLazyQuery" call, the component re-renders, changing the function "doLazyQuery" and triggering the effect and query again,
  //    so we check if it was called to avoid causing a loop of queries
  const wasInvoked = called && isEqual(calledVariables, variables);
  const doDebouncedQuery = useCallback(doDebounce(doLazyQuery, 300), [query, variables]);
  const doQuery = useCallback(doLazyQuery, [query, variables]);

  useEffect(
    () => {
      if (!skip && !wasInvoked) {
        debounce || (isFirst.current && debounceFirst) ? doDebouncedQuery({ variables }) : doQuery({ variables });
        isFirst.current = false;
      }
    },
    [debounce, debounceFirst, doDebouncedQuery, doQuery, skip, variables, wasInvoked],
  );

  if (wasInvoked && !loading) {
    if (error) {
      if (error.networkError?.statusCode === HTTP_ERROR_CODE_403) {
        onRefreshToken();
      }
    }
    onFinishedQuery(!error);
  }

  return { data, loading, error, refetch, calledVariables };
};
